import { useCallback, useEffect, useMemo, useState } from 'react';
import { GetAdminBackupsAPI } from '../../../../api/AdminAPI';

/**
 * useAdminBackups
 *
 * Hook responsável por carregar a lista de backups do banco de dados (Admin Geral).
 * Sem filtros ou paginação: a lista completa é retornada pela API de uma vez,
 * já ordenada do mais recente para o mais antigo.
 *
 * @returns {{
 *   backups: Array,
 *   total: number,
 *   totalSize: number,
 *   latest: Object|null,
 *   directoryExists: boolean,
 *   isLoading: boolean,
 *   error: string|null,
 *   refresh: Function,
 * }}
 */
function useAdminBackups() {
  const [backups, setBackups] = useState([]);
  const [directoryExists, setDirectoryExists] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBackups = useCallback(() => {
    setIsLoading(true);
    setError(null);
    GetAdminBackupsAPI((response) => {
      if (!response || response.success === false) {
        setError(response?.error || 'Erro ao carregar os backups. Tente novamente.');
        setIsLoading(false);
        return;
      }
      setBackups(response.backups || []);
      setDirectoryExists(response.directory_exists !== false);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    fetchBackups();
  }, [fetchBackups]);

  const { total, totalSize, latest } = useMemo(() => {
    return {
      total: backups.length,
      totalSize: backups.reduce((sum, backup) => sum + (backup.size || 0), 0),
      latest: backups.length > 0 ? backups[0] : null,
    };
  }, [backups]);

  return {
    backups,
    total,
    totalSize,
    latest,
    directoryExists,
    isLoading,
    error,
    refresh: fetchBackups,
  };
}

export default useAdminBackups;
